import React from "react";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useTheme } from "@/hooks/use-theme";

/**
 * Opacity percentages used to derive the accent tints from the brand primary.
 * Dark mode needs a stronger mix because a translucent accent over a dark
 * surface reads fainter than the same mix over white.
 */
const BORDER_MIX = { light: 14, dark: 22 } as const;
const HOVER_MIX = { light: 28, dark: 38 } as const;
const RING_MIX = 14;

/**
 * The brand-accent custom properties shared by focusable input surfaces
 * (the chat composer and the Workflow Builder search field).
 *
 * The values are read from the active brand rather than a static token
 * because each tenant's primary differs, and `color-mix` keeps the resting
 * and hover borders as tints of that primary instead of a neutral grey.
 *
 * Consumers spread the result into `style` and reference the variables with
 * Tailwind's arbitrary-property syntax, for example
 * `border-(--prompt-accent-border) focus-within:border-(--prompt-accent)`.
 */
export interface PromptAccentStyle extends React.CSSProperties {
  "--prompt-accent": string;
  "--prompt-accent-border": string;
  "--prompt-accent-hover": string;
  "--prompt-accent-ring": string;
}

export const usePromptAccent = (): PromptAccentStyle => {
  const { brand } = useBrand();
  const { isDark } = useTheme();

  return React.useMemo(() => {
    const accent = isDark ? brand.colours.dark.primary : brand.colours.light.primary;
    const mode = isDark ? "dark" : "light";

    return {
      "--prompt-accent": accent,
      "--prompt-accent-border": `color-mix(in srgb, ${accent} ${BORDER_MIX[mode]}%, transparent)`,
      "--prompt-accent-hover": `color-mix(in srgb, ${accent} ${HOVER_MIX[mode]}%, transparent)`,
      "--prompt-accent-ring": `color-mix(in srgb, ${accent} ${RING_MIX}%, transparent)`,
    };
  }, [brand, isDark]);
};
